// src/utils/sentryFilters.js

/**
 * Sentry Error Filtering and PII Scrubbing Module
 *
 * This module provides comprehensive filtering and data scrubbing for Sentry events
 * to reduce PII exposure and preserve the free plan quota (5,000 errors/month).
 *
 * Features:
 * - PII scrubbing for emails, passwords, tokens, and credit cards
 * - Aggressive error filtering to preserve quota
 * - Network timeout and transient error filtering
 * - Development resource error filtering (404s for .js/.css/.png)
 * - Third-party library warning filtering
 * - Bot/crawler traffic filtering (YandexBot, Googlebot, etc.)
 * - Strict mode for quota management
 *
 * Requirements: 1.7, 10.3, 10.6, 11.1, 11.2
 */

import logger from './logger';

/**
 * Sensitive data patterns for PII scrubbing
 * These patterns match common PII found in error messages and breadcrumbs
 */
const SENSITIVE_PATTERNS = {
  // Email pattern - matches most email formats
  email: /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/gi,

  // Password-related patterns
  password: /("password"\s*:\s*")([^"]+)(")/gi,
  passwordParam: /([?&]password=)([^&\s]+)/gi,

  // Token and API key patterns
  token: /("(?:token|auth|authorization|bearer)"\s*:\s*")([^"]+)(")/gi,
  apiKey: /("(?:api[-_]?key|apikey)"\s*:\s*")([^"]+)(")/gi,
  secret: /("(?:secret|client_secret)"\s*:\s*")([^"]+)(")/gi,

  // Credit card pattern (basic - matches 13-19 digit sequences)
  creditCard: /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,

  // Session and auth tokens in URLs
  sessionToken: /([?&](?:session|sid|auth)=)([^&\s]+)/gi,
};

/**
 * Sensitive object keys that should have their values redacted
 */
const SENSITIVE_KEYS = [
  'password',
  'passwd',
  'pwd',
  'secret',
  'token',
  'auth',
  'authorization',
  'bearer',
  'api_key',
  'apikey',
  'api-key',
  'apiKey',
  'access_token',
  'accessToken',
  'refresh_token',
  'refreshToken',
  'session',
  'sessionid',
  'session_id',
  'credit_card',
  'creditcard',
  'card_number',
  'cardnumber',
  'cvv',
  'ssn',
];

/**
 * Network error codes that indicate transient failures
 * These should be filtered to preserve quota
 */
const TRANSIENT_HTTP_CODES = [408, 429, 503, 504];

/**
 * Network error codes/strings that indicate transient connection issues
 */
const TRANSIENT_NETWORK_ERRORS = [
  'ETIMEDOUT',
  'ECONNRESET',
  'ECONNREFUSED',
  'ENOTFOUND',
  'ENETUNREACH',
  'EHOSTUNREACH',
  'Network request failed',
  'fetch failed',
];

/**
 * Known bot/crawler user agent patterns
 * These are filtered to reduce noise from non-human traffic
 */
const BOT_PATTERNS = [
  'bot',
  'crawler',
  'spider',
  'slurp',
  'googlebot',
  'bingbot',
  'yandex',
  'baidu',
  'duckduck',
  'facebookexternalhit',
  'twitterbot',
  'linkedinbot',
  'whatsapp',
  'telegrambot',
  'applebot',
  'semrush',
  'ahrefsbot',
  'mj12bot',
  'dotbot',
  'petalbot',
  'bytespider',
];

/**
 * File extensions for development resource errors to filter
 */
const DEVELOPMENT_RESOURCE_EXTENSIONS = [
  '.js',
  '.css',
  '.png',
  '.jpg',
  '.jpeg',
  '.svg',
  '.woff',
  '.woff2',
  '.map',
];

/**
 * Scrubs sensitive data from a text string
 *
 * @param {string} text - Text that may contain PII
 * @returns {string} - Text with PII replaced by [REDACTED]
 */
export function scrubSensitiveData(text) {
  if (!text || typeof text !== 'string') {
    return text;
  }

  let scrubbedText = text;

  // Replace all sensitive patterns
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.email, '[EMAIL_REDACTED]');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.password, '$1[REDACTED]$3');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.passwordParam, '$1[REDACTED]');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.token, '$1[REDACTED]$3');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.apiKey, '$1[REDACTED]$3');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.secret, '$1[REDACTED]$3');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.creditCard, '[CARD_REDACTED]');
  scrubbedText = scrubbedText.replace(SENSITIVE_PATTERNS.sessionToken, '$1[REDACTED]');

  return scrubbedText;
}

/**
 * Recursively scrubs sensitive data from object keys and values
 *
 * @param {any} data - Data object to scrub
 * @param {number} depth - Current recursion depth (prevents infinite loops)
 * @returns {any} - Scrubbed data
 */
export function scrubObjectData(data, depth = 0) {
  // Prevent infinite recursion
  if (depth > 10) {
    return '[MAX_DEPTH_REACHED]';
  }

  // Handle null/undefined
  if (data == null) {
    return data;
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => scrubObjectData(item, depth + 1));
  }

  // Handle objects
  if (typeof data === 'object') {
    const scrubbed = {};
    for (const [key, value] of Object.entries(data)) {
      const lowerKey = key.toLowerCase();

      // Check if key is sensitive
      if (SENSITIVE_KEYS.some((sensitiveKey) => lowerKey.includes(sensitiveKey))) {
        scrubbed[key] = '[REDACTED]';
      } else if (typeof value === 'string') {
        // Scrub string values
        scrubbed[key] = scrubSensitiveData(value);
      } else if (typeof value === 'object') {
        // Recursively scrub nested objects
        scrubbed[key] = scrubObjectData(value, depth + 1);
      } else {
        scrubbed[key] = value;
      }
    }
    return scrubbed;
  }

  // Handle strings
  if (typeof data === 'string') {
    return scrubSensitiveData(data);
  }

  // Return primitive values unchanged
  return data;
}

/**
 * Checks if an error is a network timeout or rate limit error
 *
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object containing original exception
 * @returns {boolean} - True if error should be filtered
 */
export function isNetworkTimeoutError(event, hint) {
  // Check HTTP status codes in event tags
  if (event.tags?.http_status) {
    const status = parseInt(event.tags.http_status, 10);
    if (TRANSIENT_HTTP_CODES.includes(status)) {
      return true;
    }
  }

  // Check exception message for transient error strings
  const originalException = hint?.originalException;
  if (originalException) {
    const errorMessage = originalException.message || originalException.toString();

    // Check for transient network error codes/messages
    if (TRANSIENT_NETWORK_ERRORS.some((pattern) => errorMessage.includes(pattern))) {
      return true;
    }

    // Check for HTTP status codes in error message
    const statusMatch = errorMessage.match(/status\s+code\s+(\d{3})/i);
    if (statusMatch) {
      const status = parseInt(statusMatch[1], 10);
      if (TRANSIENT_HTTP_CODES.includes(status)) {
        return true;
      }
    }
  }

  // Check event exception values
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      if (exception.value) {
        if (TRANSIENT_NETWORK_ERRORS.some((pattern) => exception.value.includes(pattern))) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Checks if an error is a 404 resource error in development
 * (e.g., missing .js, .css, .png files during hot reload)
 *
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object
 * @returns {boolean} - True if error should be filtered
 */
export function is404ResourceError(event, hint) {
  // Only filter in development
  if (!__DEV__) {
    return false;
  }

  // Check for 404 status
  const is404 = event.tags?.http_status === '404' || event.tags?.http_status === 404;

  if (!is404) {
    return false;
  }

  // Check if error involves a static resource
  const originalException = hint?.originalException;
  if (originalException) {
    const errorMessage = originalException.message || originalException.toString();

    // Check if message contains a file extension
    if (DEVELOPMENT_RESOURCE_EXTENSIONS.some((ext) => errorMessage.includes(ext))) {
      return true;
    }
  }

  // Check event breadcrumbs for resource URLs
  if (event.breadcrumbs) {
    for (const breadcrumb of event.breadcrumbs) {
      if (breadcrumb.data?.url) {
        if (DEVELOPMENT_RESOURCE_EXTENSIONS.some((ext) => breadcrumb.data.url.includes(ext))) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Checks if an error is a warning from third-party libraries (node_modules)
 *
 * @param {Object} event - Sentry event object
 * @returns {boolean} - True if error should be filtered
 */
export function isThirdPartyWarning(event) {
  // Only filter warning-level events
  if (event.level !== 'warning') {
    return false;
  }

  // Check if any stack frame is from node_modules
  if (event.exception?.values) {
    for (const exception of event.exception.values) {
      if (exception.stacktrace?.frames) {
        for (const frame of exception.stacktrace.frames) {
          if (frame.filename?.includes('node_modules')) {
            return true;
          }
        }
      }
    }
  }

  return false;
}

/**
 * Checks if an error originated from a bot or web crawler
 * Bots often fail to properly execute JavaScript, causing spurious errors
 *
 * @param {Object} event - Sentry event object
 * @returns {boolean} - True if error is from a bot/crawler
 */
export function isBotOrCrawler(event) {
  // Check browser tag (e.g., "YandexBot 3.0")
  const browser = event.tags?.browser || event.contexts?.browser?.name || '';
  const browserLower = browser.toLowerCase();

  if (BOT_PATTERNS.some((pattern) => browserLower.includes(pattern))) {
    return true;
  }

  // Check device family tag (e.g., "Spider")
  const deviceFamily = event.tags?.['device.family'] || event.contexts?.device?.family || '';
  const deviceFamilyLower = deviceFamily.toLowerCase();

  if (deviceFamilyLower === 'spider' || deviceFamilyLower === 'robot') {
    return true;
  }

  // Check user agent in request headers if available
  const userAgent = event.request?.headers?.['User-Agent'] || event.request?.headers?.['user-agent'] || '';
  const userAgentLower = userAgent.toLowerCase();

  if (BOT_PATTERNS.some((pattern) => userAgentLower.includes(pattern))) {
    return true;
  }

  return false;
}

/**
 * Gets the filter mode from environment
 * Supports 'normal' (default) or 'strict' (aggressive filtering)
 *
 * @returns {string} - Filter mode ('normal' or 'strict')
 */
function getFilterMode() {
  // Access environment variable at build time
  const mode = process.env.EXPO_PUBLIC_SENTRY_FILTER_MODE || 'normal';
  return mode.toLowerCase();
}

/**
 * Applies strict mode sampling logic
 * Strict mode: prod errors 100%, dev errors 0%, warnings 10% sample
 *
 * @param {Object} event - Sentry event object
 * @returns {boolean} - True if event should be sent, false if filtered
 */
function applyStrictModeFilter(event) {
  const isProduction = !__DEV__;
  const level = event.level || 'error';

  // Production errors: always capture (100%)
  if (isProduction && (level === 'error' || level === 'fatal')) {
    return true;
  }

  // Development errors: never capture (0%)
  if (!isProduction && (level === 'error' || level === 'fatal')) {
    logger.log('[Sentry] Strict mode: filtering development error', event.event_id);
    return false;
  }

  // Warnings: 10% sample rate
  if (level === 'warning') {
    const shouldCapture = Math.random() < 0.1;
    if (!shouldCapture) {
      logger.log('[Sentry] Strict mode: sampling out warning', event.event_id);
    }
    return shouldCapture;
  }

  // Info and debug: never capture
  if (level === 'info' || level === 'debug' || level === 'log') {
    return false;
  }

  // Default: capture
  return true;
}

/**
 * Main filter function for Sentry events
 * This is the beforeSend hook that processes all events before sending to Sentry
 *
 * Responsibilities:
 * 1. Scrub PII from event data (GDPR compliance)
 * 2. Filter network timeout/transient errors
 * 3. Filter development resource 404s
 * 4. Filter third-party warnings
 * 5. Apply strict mode sampling if enabled
 *
 * @param {Object} event - Sentry event object
 * @param {Object} hint - Sentry hint object containing original exception
 * @returns {Object|null} - Modified event or null to drop the event
 */
export function filterSentryEvent(event, hint) {
  try {
    // 1. Check if this is a transient network error (filter out)
    if (isNetworkTimeoutError(event, hint)) {
      logger.log('[Sentry] Filtering network timeout error:', event.event_id);
      return null;
    }

    // 2. Check if this is a 404 resource error in development (filter out)
    if (is404ResourceError(event, hint)) {
      logger.log('[Sentry] Filtering development 404 resource error:', event.event_id);
      return null;
    }

    // 3. Check if this is a third-party warning (filter out)
    if (isThirdPartyWarning(event)) {
      logger.log('[Sentry] Filtering third-party warning:', event.event_id);
      return null;
    }

    // 4. Check if this is from a bot/crawler (filter out)
    if (isBotOrCrawler(event)) {
      logger.log('[Sentry] Filtering bot/crawler error:', event.event_id);
      return null;
    }

    // 5. Apply strict mode filtering if enabled
    const filterMode = getFilterMode();
    if (filterMode === 'strict') {
      const shouldSend = applyStrictModeFilter(event);
      if (!shouldSend) {
        return null;
      }
    }

    // 6. Scrub PII from event data
    // Scrub exception messages
    if (event.exception?.values) {
      event.exception.values = event.exception.values.map((exception) => ({
        ...exception,
        value: scrubSensitiveData(exception.value),
      }));
    }

    // Scrub event message
    if (event.message) {
      event.message = scrubSensitiveData(event.message);
    }

    // Scrub breadcrumbs
    if (event.breadcrumbs) {
      event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => ({
        ...breadcrumb,
        message: breadcrumb.message ? scrubSensitiveData(breadcrumb.message) : breadcrumb.message,
        data: breadcrumb.data ? scrubObjectData(breadcrumb.data) : breadcrumb.data,
      }));
    }

    // Scrub request data
    if (event.request) {
      event.request = scrubObjectData(event.request);
    }

    // Scrub extra context
    if (event.extra) {
      event.extra = scrubObjectData(event.extra);
    }

    // Scrub tags
    if (event.tags) {
      event.tags = scrubObjectData(event.tags);
    }

    // Scrub user context (email should already be hashed, but double-check)
    if (event.user?.email) {
      // If email is not already a hash (64 hex chars), redact it
      if (!/^[a-f0-9]{64}$/i.test(event.user.email)) {
        event.user.email = '[REDACTED]';
      }
    }

    logger.log('[Sentry] Event passed filters:', event.event_id, 'Level:', event.level);
    return event;
  } catch (error) {
    // If filtering fails, log error but send event anyway (fail-safe)
    logger.error('[Sentry] Error in filterSentryEvent:', error);
    return event;
  }
}

/**
 * Export all functions for testing
 */
export default {
  filterSentryEvent,
  scrubSensitiveData,
  scrubObjectData,
  isNetworkTimeoutError,
  is404ResourceError,
  isThirdPartyWarning,
  isBotOrCrawler,
};
